import React from 'react';

import { FormattedMessage } from 'react-intl';

import { Button } from 'components/buttons/Button';

import type { TwoFactorProvider } from '../../types/login';
import { BackToLoginLink } from '../common/BackToLoginLink';
import { ErrorMessage } from '../common/ErrorMessage';

interface ProviderSelectStepProps {
    providers: TwoFactorProvider[];
    selectedProvider: TwoFactorProvider | null;
    isLoading: boolean;
    isSending: boolean;
    error?: string;
    onSelect: (provider: TwoFactorProvider) => void;
    onSendCode: () => void;
    onPhoneSetup: () => void;
    onBack: () => void;
}

export const ProviderSelectStep = ({
    providers,
    selectedProvider,
    isLoading,
    isSending,
    error,
    onSelect,
    onSendCode,
    onPhoneSetup,
    onBack,
}: ProviderSelectStepProps) => {
    if (isLoading) {
        return (
            <div className="tw-flex tw-justify-center tw-py-48">
                <div className="tw-animate-spin tw-h-24 tw-w-24 tw-border-2 tw-border-interactive tw-border-t-transparent tw-rounded-full" />
            </div>
        );
    }

    const hasPhoneProvider = providers.some(
        (p) => p.toLowerCase() === 'phone',
    );

    return (
        <div>
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.twoFactor.title"
                    defaultMessage="Two-factor authentication"
                />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-24">
                <FormattedMessage
                    id="login.twoFactor.description"
                    defaultMessage="Choose how you'd like to receive your verification code."
                />
            </p>

            <div className="tw-flex tw-flex-col tw-gap-8 tw-mb-16">
                {providers.map((provider) => (
                    <ProviderCard
                        key={provider}
                        provider={provider}
                        isSelected={selectedProvider === provider}
                        onSelect={() => onSelect(provider)}
                    />
                ))}
            </div>

            <ErrorMessage message={error} />

            <div className="tw-mt-16">
                <Button
                    variant="primary"
                    disabled={!selectedProvider || isSending}
                    wrapperClasses="tw-w-full"
                    onClick={onSendCode}
                >
                    <FormattedMessage id="login.twoFactor.sendCode" defaultMessage="Send code" />
                </Button>
            </div>

            {!hasPhoneProvider && (
                <button
                    type="button"
                    onClick={onPhoneSetup}
                    className="tw-text-primary tw-text-s tw-mt-12 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-underline"
                >
                    <FormattedMessage
                        id="login.twoFactor.addPhone"
                        defaultMessage="Add phone number for verification"
                    />
                </button>
            )}

            <BackToLoginLink onClick={onBack} />
        </div>
    );
};

// -------------------------------------------------------------------
// Provider card — now renders a simple string provider name
// -------------------------------------------------------------------

interface ProviderCardProps {
    provider: TwoFactorProvider;
    isSelected: boolean;
    onSelect: () => void;
}

const ProviderCard = ({ provider, isSelected, onSelect }: ProviderCardProps) => {
    const selectedClasses = isSelected
        ? 'tw-border-interactive tw-ring-1 tw-ring-interactive'
        : 'tw-border-default hover:tw-bg-neutral-hovered';

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`tw-flex tw-items-center tw-gap-12 tw-p-12 tw-rounded tw-border tw-w-full tw-bg-neutral tw-text-left tw-cursor-pointer tw-transition-colors ${selectedClasses}`}
        >
            <div className="tw-text-s tw-font-medium tw-text-neutral">{provider}</div>
        </button>
    );
};
